/**
 * Pipeline HTML behavior from scikit-learn estimator repr.
 * Source: https://github.com/scikit-learn/scikit-learn/blob/1.8.X/sklearn/utils/_repr_html/estimator.js
 * Inlined to avoid executing untrusted <script> from pipeline HTML.
 */

export function copyToClipboard(text: string, element: HTMLElement): boolean {
  const toggleableContent = element.closest(".sk-toggleable__content");
  const paramPrefix = toggleableContent
    ? ((toggleableContent as HTMLElement).dataset.paramPrefix ?? "")
    : "";
  const fullParamName = paramPrefix ? `${paramPrefix}${text}` : text;

  const originalStyle = element.style.cssText;
  const computedStyle = window.getComputedStyle(element);
  const originalWidth = computedStyle.width;
  const originalHTML = element.innerHTML.replace("Copied!", "");

  navigator.clipboard
    .writeText(fullParamName)
    .then(() => {
      element.style.width = originalWidth;
      element.style.color = "green";
      element.innerHTML = "Copied!";
      setTimeout(() => {
        element.innerHTML = originalHTML;
        element.style.cssText = originalStyle;
      }, 2000);
    })
    .catch((err) => {
      console.error("Failed to copy:", err);
      element.style.color = "red";
      element.innerHTML = "Failed!";
      setTimeout(() => {
        element.innerHTML = originalHTML;
        element.style.cssText = originalStyle;
      }, 2000);
    });
  return false;
}

/** Compute relative luminance (0–255) from rgb components. */
function lumaFromRgb(r: number, g: number, b: number): number {
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

/** Parse computed color (rgb/rgba) and return luma or null. */
function parseLuma(computed: string): number | null {
  const rgb = computed.match(/^rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgb && rgb[1] != null && rgb[2] != null && rgb[3] != null) {
    return lumaFromRgb(parseFloat(rgb[1]), parseFloat(rgb[2]), parseFloat(rgb[3]));
  }
  return null;
}

/**
 * Adapted from Skrub. Detects theme from VSCode/Jupyter, app root, then container
 * ancestry (background or text color luma), so the pipeline matches the app theme.
 * @returns "light" or "dark"
 */
function detectTheme(container: Element): "light" | "dark" {
  const body = document.querySelector("body");
  if (!body) return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";

  const themeKindAttr = body.getAttribute("data-vscode-theme-kind");
  const themeNameAttr = body.getAttribute("data-vscode-theme-name");
  if (themeKindAttr && themeNameAttr) {
    const themeKind = themeKindAttr.toLowerCase();
    const themeName = themeNameAttr.toLowerCase();
    if (themeKind.includes("dark") || themeName.includes("dark")) return "dark";
    if (themeKind.includes("light") || themeName.includes("light")) return "light";
  }

  if (body.getAttribute("data-jp-theme-light") === "false") return "dark";
  if (body.getAttribute("data-jp-theme-light") === "true") return "light";

  // App root theme (e.g. data-theme or .dark on html)
  const html = document.documentElement;
  const dataTheme = html.getAttribute("data-theme");
  if (dataTheme) {
    const t = dataTheme.toLowerCase();
    if (t === "dark" || t.includes("dark")) return "dark";
    if (t === "light" || t.includes("light")) return "light";
  }
  if (html.classList.contains("dark")) return "dark";
  if (html.classList.contains("light")) return "light";

  // Walk up from container and use background or color luma from the first usable ancestor
  let el: Element | null = container;
  while (el) {
    const style = window.getComputedStyle(el, null);
    const bg = style.getPropertyValue("background-color");
    if (bg && bg !== "transparent" && bg !== "rgba(0, 0, 0, 0)") {
      const l = parseLuma(bg);
      if (l != null) {
        if (l > 200) return "light";
        if (l < 80) return "dark";
      }
    }
    const color = style.getPropertyValue("color");
    if (color) {
      const l = parseLuma(color);
      if (l != null) {
        // Dark text on light background → light theme; light text on dark background → dark theme
        if (l > 180) return "dark";
        if (l < 75) return "light";
      }
    }
    el = el.parentElement;
  }

  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function forceTheme(container: HTMLElement): void {
  const theme = detectTheme(container);
  container.classList.add(theme);
}

/**
 * Set up copy-to-clipboard and theme for a pipeline HTML container.
 * Call after the pipeline HTML is rendered. Exposes copyToClipboard on window
 * so inline onclick handlers in the pipeline HTML work.
 */
export function initPipelineBehavior(container: HTMLElement | null): void {
  if (!container) return;

  (window as unknown as { copyToClipboard: typeof copyToClipboard }).copyToClipboard =
    copyToClipboard;

  container.querySelectorAll(".copy-paste-icon").forEach((element) => {
    const el = element as HTMLElement;
    const toggleableContent = el.closest(".sk-toggleable__content");
    const paramPrefix = toggleableContent
      ? ((toggleableContent as HTMLElement).dataset.paramPrefix ?? "")
      : "";
    const nextSibling = el.parentElement?.nextElementSibling;
    const paramName = nextSibling ? (nextSibling.textContent?.trim().split(" ")[0] ?? "") : "";
    const fullParamName = paramPrefix ? `${paramPrefix}${paramName}` : paramName;
    el.setAttribute("title", fullParamName);
  });

  forceTheme(container);
}

/** Remove all <script> tags from pipeline HTML to avoid executing untrusted code. */
export function stripScriptTags(html: string): string {
  return html.replace(/<script\b[\s\S]*?<\/script>/gi, "");
}
